import { Injectable } from '@angular/core';
import { Observable, delay, of, throwError } from 'rxjs';
import { ActionApi, ActionPage } from '../api/action-api.token';
import {
  CreateActionRequest,
  UpdateActionRequest,
} from '../api/action-api.model';
import { CarePlanAction, KNOWN_ACTION_STATUS } from '../actions.model';

@Injectable({ providedIn: 'root' })
export class MockActionApiService implements ActionApi {
  private readonly _actions = new Map<string, CarePlanAction[]>();
  private _nextId = 2000;
  private _latencyMs = 0;
  private _nextError: Error | null = null;

  listActions(
    caseId: string,
    limit: number,
    offset: number,
  ): Observable<ActionPage> {
    const error = this._consumeError();
    if (error) return throwError(() => error);
    const actions = this._actionsFor(caseId);
    const items = this._clone(actions.slice(offset, offset + limit));
    return of({
      items,
      consumedItems: items.length,
      pagination: { limit, offset, totalItems: actions.length },
    }).pipe(delay(this._latencyMs));
  }

  listClientActions(
    clientId: number,
    statusCodes: readonly string[],
    limit: number,
    offset: number,
  ): Observable<ActionPage> {
    const error = this._consumeError();
    if (error) return throwError(() => error);
    const statuses = new Set(statusCodes);
    const actions = [...this._actions.values()]
      .flat()
      .filter(
        (action) =>
          action.clientId === clientId &&
          action.statusCode != null &&
          statuses.has(action.statusCode),
      );
    const items = this._clone(actions.slice(offset, offset + limit));
    return of({
      items,
      consumedItems: items.length,
      pagination: { limit, offset, totalItems: actions.length },
    }).pipe(delay(this._latencyMs));
  }

  getAction(actionId: string): Observable<CarePlanAction> {
    const error = this._consumeError();
    if (error) return throwError(() => error);
    const action = [...this._actions.values()]
      .flat()
      .find((item) => item.id === actionId);
    return action
      ? of({ ...action }).pipe(delay(this._latencyMs))
      : throwError(() => new Error('This action is no longer available.'));
  }

  createAction(body: CreateActionRequest): Observable<CarePlanAction> {
    const error = this._consumeError();
    if (error) return throwError(() => error);
    const created: CarePlanAction = {
      id: String(this._nextId++),
      clientId: body.clientId,
      caseId: body.caseId == null ? null : String(body.caseId),
      title: body.name,
      description: body.description ?? null,
      actionTypeCode: body.actionTypeCode,
      assignedToId: body.assignedToId ?? 'current-okta-user',
      statusCode: KNOWN_ACTION_STATUS.OPEN,
      dueDate: body.dueDate,
      priorityCode: body.priorityCode,
      sourceTypeCode: 'HUMAN_FA',
      sourceId: 1,
      communicationPreferenceCode: body.communicationPreferenceCode ?? null,
      clientPromise: body.clientPromise,
      completedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: null,
    };
    this._actionsFor(String(body.caseId)).push(created);
    return of({ ...created }).pipe(delay(this._latencyMs));
  }

  updateAction(
    actionId: string,
    body: UpdateActionRequest,
  ): Observable<CarePlanAction> {
    const error = this._consumeError();
    if (error) return throwError(() => error);
    const current = [...this._actions.values()]
      .flat()
      .find((action) => action.id === actionId);
    if (!current)
      return throwError(() => new Error('This action is no longer available.'));
    if (body.name !== undefined) current.title = body.name;
    if (body.description !== undefined) current.description = body.description;
    if (body.actionTypeCode !== undefined)
      current.actionTypeCode = body.actionTypeCode;
    if (body.assignedToId !== undefined)
      current.assignedToId = body.assignedToId;
    if (body.statusCode !== undefined) current.statusCode = body.statusCode;
    if (body.dueDate !== undefined) current.dueDate = body.dueDate;
    if (body.priorityCode !== undefined)
      current.priorityCode = body.priorityCode;
    if (body.communicationPreferenceCode !== undefined) {
      current.communicationPreferenceCode = body.communicationPreferenceCode;
    }
    if (body.clientPromise !== undefined)
      current.clientPromise = body.clientPromise;
    current.updatedAt = new Date().toISOString();
    return of({ ...current }).pipe(delay(this._latencyMs));
  }

  seed(caseId: string, actions: readonly CarePlanAction[]): void {
    this._actions.set(caseId, this._clone(actions));
  }

  setLatency(latencyMs: number): void {
    this._latencyMs = Math.max(0, latencyMs);
  }

  failNext(message = "Actions couldn't be loaded right now."): void {
    this._nextError = new Error(message);
  }

  private _actionsFor(caseId: string): CarePlanAction[] {
    let actions = this._actions.get(caseId);
    if (!actions) {
      actions = [];
      this._actions.set(caseId, actions);
    }
    return actions;
  }

  private _consumeError(): Error | null {
    const error = this._nextError;
    this._nextError = null;
    return error;
  }

  private _clone(actions: readonly CarePlanAction[]): CarePlanAction[] {
    return actions.map((action) => ({ ...action }));
  }
}
